import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ArrowRight,
  ArrowUpRight,
  Wrench,
  Dumbbell,
  Hammer,
  Building2,
  Building,
  Landmark,
  Hotel,
  GraduationCap,
  Home,
  ClipboardCheck,
  FileText,
  Cog,
  Camera,
  Instagram,
  Mail,
  MessageCircle,
  Phone,
  Menu,
  X,
} from "lucide-react";

// ---------- Assets ----------
const LOGO_URL = "/logo-dark.png";
const Wordmark = ({ className = "" }) => (
  <img src={LOGO_URL} alt="All Active" className={`h-8 md:h-9 w-auto select-none ${className}`} />
);
const HERO_IMG =
  "https://images.pexels.com/photos/29224211/pexels-photo-29224211.jpeg?auto=compress&cs=tinysrgb&w=1920";
const SERVICE_MAINTENANCE_IMG =
  "https://images.unsplash.com/photo-1576678927484-cc907957088c?auto=format&fit=crop&w=1600&q=80";
const SERVICE_RENOVATION_IMG =
  "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?auto=format&fit=crop&w=1600&q=80";
const SERVICE_ASSEMBLY_IMG =
  "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1600&q=80";

const GALLERY_FALLBACK = [
  "https://allactive.cl/wp-content/uploads/2024/05/WhatsApp-Image-2024-05-24-at-11.42.01-2-1024x768.jpeg",
  "https://allactive.cl/wp-content/uploads/2024/05/WhatsApp-Image-2024-05-24-at-11.42.00-1-1024x768.jpeg",
  "https://allactive.cl/wp-content/uploads/2024/06/mantenimiento-de-maquinas-de-gimnasio.jpg",
  "https://allactive.cl/wp-content/uploads/2024/06/Mantenimiento-Servicio-Tecnico-Gimnasios.jpeg.webp",
  "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=1200&q=80",
];

const SEGMENTS = [
  { icon: Building2, label: "Edificios", desc: "Comunidades residenciales con gimnasios propios." },
  { icon: Landmark, label: "Municipalidades", desc: "Plazas activas, gimnasios comunitarios y espacios públicos." },
  { icon: Building, label: "Empresas", desc: "Zonas fitness para colaboradores y bienestar corporativo." },
  { icon: Hotel, label: "Hoteles", desc: "Gimnasios de huéspedes disponibles 24/7 sin interrupciones." },
  { icon: GraduationCap, label: "Colegios", desc: "Equipamiento seguro para alumnos y personal docente." },
  { icon: Home, label: "Privados", desc: "Gimnasios en casa con soporte técnico profesional." },
];

const PROCESS = [
  { n: "01", icon: ClipboardCheck, title: "Diagnóstico", body: "Visitamos, inspeccionamos cada máquina y levantamos un reporte técnico detallado con fotos." },
  { n: "02", icon: FileText, title: "Cotización", body: "Recibes una propuesta clara, sin sorpresas: alcance, materiales y tiempos precisos." },
  { n: "03", icon: Cog, title: "Ejecución", body: "Técnicos especializados intervienen con repuestos originales y herramientas profesionales." },
  { n: "04", icon: Camera, title: "Reporte con fotos", body: "Al terminar recibes un informe con evidencia visual — trazabilidad total del trabajo." },
];

const REASONS = [
  { value: "visita_mantencion", label: "Quiero una visita de mantención" },
  { value: "cotizacion", label: "Cotizar gimnasio nuevo o renovación" },
  { value: "comercial", label: "Consultas comerciales" },
  { value: "otro", label: "Otro" },
];

const WHATSAPP_NUMBER = "+56984713814";
const WHATSAPP_URL = `https://wa.me/56984713814?text=${encodeURIComponent(
  "Hola, vengo del sitio de All Active y quiero más info sobre mantención / renovación de mi gimnasio."
)}`;

// ---------- Reusable primitives ----------
const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const Kicker = ({ children, className = "" }) => (
  <div className={`font-mono-kpi text-[10px] uppercase tracking-[0.28em] text-[#FF5A00] ${className}`}>
    {children}
  </div>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative px-6 md:px-10 lg:px-16 py-24 md:py-32 ${className}`}>
    {children}
  </section>
);

// ---------- Ambient hero canvas (subtle orange organic lines) ----------
function HeroCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.scale(DPR, DPR);
    };
    resize();
    window.addEventListener("resize", resize);

    // Particle field: subtle orange dots + connecting lines
    const N = 42;
    const particles = Array.from({ length: N }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      r: Math.random() * 1.6 + 0.6,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < N; i++) {
        const p = particles[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,90,0,0.55)";
        ctx.fill();
        for (let j = i + 1; j < N; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 160) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(255,90,0,${(1 - d / 160) * 0.18})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={ref}
      className="absolute inset-0 w-full h-full pointer-events-none opacity-70 mix-blend-screen"
      aria-hidden="true"
    />
  );
}

// ---------- Floating WhatsApp CTA ----------
function WhatsAppFab() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noreferrer"
      aria-label="Contactar por WhatsApp"
      data-testid="whatsapp-fab"
      className="fixed z-50 bottom-6 right-6 md:bottom-8 md:right-8 group"
    >
      <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping" aria-hidden="true" />
      <span className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_-8px_rgba(37,211,102,0.6)] group-hover:scale-110 transition-transform">
        <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-8 md:h-8" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </span>
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-black/85 text-white text-xs font-mono-kpi uppercase tracking-[0.18em] px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden md:block">
        Escríbenos por WhatsApp
      </span>
    </a>
  );
}

// ---------- Sections ----------
function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#segmentos", label: "Segmentos" },
    { href: "#servicios", label: "Servicios" },
    { href: "#proceso", label: "Proceso" },
    { href: "#trabajos", label: "Trabajos" },
    { href: "#contacto", label: "Contacto" },
  ];

  return (
    <header
      data-testid="landing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3">
          <Wordmark className="h-8 md:h-9" />
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="font-mono-kpi text-[11px] uppercase tracking-[0.24em] text-white/70 hover:text-[#FF5A00] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contacto"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04D00] text-white font-mono-kpi text-[11px] uppercase tracking-[0.24em] px-5 py-2.5 transition-colors"
          >
            Solicitar visita
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </nav>
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menú"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 space-y-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block font-mono-kpi text-xs uppercase tracking-[0.24em] text-white/80 hover:text-[#FF5A00]"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contacto"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-2 bg-[#FF5A00] text-white font-mono-kpi text-xs uppercase tracking-[0.24em] px-5 py-2.5"
          >
            Solicitar visita <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </header>
  );
}

function Hero() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={heroRef}
      id="top"
      data-testid="landing-hero"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A] text-white"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Gimnasio profesional"
          className="w-full h-full object-cover object-center opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        <HeroCanvas />
        <div className="absolute inset-0 grain-overlay" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-40 md:pt-52 pb-24 min-h-screen flex flex-col justify-end">
        <div className="max-w-5xl">
          <Reveal>
            <Kicker className="mb-6">
              // Servicio técnico profesional · Chile
            </Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-tactical font-black uppercase leading-[0.9] tracking-tight text-[13vw] md:text-[9vw] lg:text-[7.5vw]">
              Mantenemos<br />
              tu gimnasio<br />
              <span className="text-[#FF5A00]">activo.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-8 max-w-xl text-base md:text-lg text-white/80 leading-relaxed">
              Mantención, reparación y renovación de gimnasios para edificios, empresas, hoteles y municipalidades.
              Planes desde mensual hasta anual — nos adaptamos a tu presupuesto y a tu comunidad.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contacto"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E04D00] text-white font-mono-kpi text-xs uppercase tracking-[0.24em] px-7 py-4 transition-colors"
              >
                Solicitar visita
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#servicios"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-3 border border-white/25 hover:border-[#FF5A00] hover:text-[#FF5A00] text-white font-mono-kpi text-xs uppercase tracking-[0.24em] px-7 py-4 transition-colors"
              >
                Ver servicios
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </motion.div>

      <div className="absolute bottom-6 inset-x-0 z-10 flex justify-center pointer-events-none">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="font-mono-kpi text-[10px] uppercase tracking-[0.32em] text-white/60"
        >
          Scroll ↓
        </motion.div>
      </div>
    </section>
  );
}

function Metrics() {
  const items = [
    { n: "+500", label: "Comunidades", sub: "Edificios y condominios que confían en nosotros" },
    { n: "+15", label: "Hoteles", sub: "Cadenas hoteleras con gimnasios operativos 24/7" },
    { n: "+30", label: "Empresas", sub: "Zonas fitness corporativas activas cada semana" },
  ];
  return (
    <section
      data-testid="landing-metrics"
      className="relative bg-[#0A0A0A] text-white border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3">
        {items.map((it, i) => (
          <Reveal key={it.label} delay={i * 0.1}>
            <div
              className={`px-8 md:px-10 py-14 md:py-20 ${
                i < items.length - 1 ? "md:border-r border-white/10" : ""
              } ${i > 0 ? "border-t md:border-t-0 border-white/10" : ""}`}
            >
              <div className="font-tactical font-black text-[16vw] md:text-[7vw] lg:text-[6vw] text-[#FF5A00] leading-none">
                {it.n}
              </div>
              <div className="mt-3 font-tactical font-black uppercase tracking-tight text-2xl">{it.label}</div>
              <div className="mt-2 text-sm text-white/60 max-w-xs">{it.sub}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Segments() {
  return (
    <Section id="segmentos" className="bg-white text-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <Kicker>// Segmentos que atendemos</Kicker>
              <h2 className="mt-4 font-tactical font-black uppercase tracking-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
                Cada espacio<br />tiene su plan.
              </h2>
            </div>
            <p className="lg:col-span-5 text-neutral-600 text-base leading-relaxed lg:pb-2">
              Adaptamos frecuencia, alcance y presupuesto según el tipo de comunidad que
              atiendes. Un colegio no funciona igual que un hotel — nosotros lo entendemos.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-200">
          {SEGMENTS.map((seg, i) => {
            const Icon = seg.icon;
            return (
              <Reveal key={seg.label} delay={i * 0.05}>
                <div
                  data-testid={`segment-${seg.label.toLowerCase()}`}
                  className="group relative border-b border-r border-neutral-200 p-8 md:p-10 min-h-[220px] hover:bg-[#0A0A0A] transition-colors cursor-default"
                >
                  <div className="text-[#FF5A00] mb-6">
                    <Icon className="w-8 h-8" strokeWidth={1.6} />
                  </div>
                  <div className="font-tactical font-black uppercase tracking-tight text-2xl md:text-3xl group-hover:text-white transition-colors">
                    {seg.label}
                  </div>
                  <div className="mt-3 text-sm text-neutral-500 group-hover:text-white/70 transition-colors leading-relaxed max-w-xs">
                    {seg.desc}
                  </div>
                  <ArrowUpRight className="absolute top-6 right-6 w-5 h-5 text-neutral-300 group-hover:text-[#FF5A00] group-hover:rotate-45 transition-all" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function Services() {
  const services = [
    {
      icon: Wrench,
      kicker: "// Servicio recurrente",
      title: "Mantención & reparación",
      body:
        "Planes mensuales, bimestrales o trimestrales. Lubricación, ajuste, revisión electrónica, cambio de piezas y reporte fotográfico cada visita.",
      bullets: ["Tarifas fijas, sin sorpresas", "Reporte con fotos incluido", "Trabajos garantizados"],
      image: SERVICE_MAINTENANCE_IMG,
    },
    {
      icon: Hammer,
      kicker: "// Instalación",
      title: "Armado & montaje",
      body:
        "Armamos e instalamos tus máquinas nuevas o compradas por tu cuenta — con anclajes, pruebas de carga y entrega calibrada lista para usar.",
      bullets: ["Armado de máquinas nuevas", "Anclajes y fijación segura", "Prueba de carga y calibración", "Retiro de embalajes"],
      image: SERVICE_ASSEMBLY_IMG,
    },
    {
      icon: Dumbbell,
      kicker: "// Proyecto puntual",
      title: "Equipamiento & renovación",
      body:
        "Renovamos gimnasios completos o entregamos zonas fitness llave en mano — desde el layout hasta la instalación final.",
      bullets: ["Diseño de layout", "Equipamiento profesional", "Instalación y anclajes", "Retiro de equipos antiguos"],
      image: SERVICE_RENOVATION_IMG,
    },
  ];
  return (
    <Section id="servicios" className="bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Kicker>// Servicios</Kicker>
          <h2 className="mt-4 font-tactical font-black uppercase tracking-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
            Tres pilares.<br />Cero improvisación.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={i * 0.1}>
                <div
                  data-testid={`service-card-${i}`}
                  className="group relative border border-white/10 hover:border-[#FF5A00] transition-colors bg-[#111] overflow-hidden"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/60 to-transparent" />
                    <div className="absolute bottom-5 left-6 flex items-center gap-3">
                      <div className="bg-[#FF5A00] p-2.5">
                        <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                      </div>
                      <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-[#FF5A00]">
                        {s.kicker}
                      </div>
                    </div>
                  </div>
                  <div className="p-8 md:p-10">
                    <h3 className="font-tactical font-black uppercase tracking-tight text-3xl md:text-4xl">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-white/70 leading-relaxed">{s.body}</p>
                    <ul className="mt-6 space-y-2">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                          <span className="w-1 h-1 bg-[#FF5A00] mt-2 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <a
                      href="#contacto"
                      className="mt-8 inline-flex items-center gap-2 font-mono-kpi text-[11px] uppercase tracking-[0.24em] text-white hover:text-[#FF5A00] transition-colors"
                    >
                      Cotizar servicio <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function Process() {
  return (
    <Section id="proceso" className="bg-white text-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Kicker>// Cómo trabajamos</Kicker>
          <h2 className="mt-4 font-tactical font-black uppercase tracking-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
            De la visita<br />al reporte final.
          </h2>
        </Reveal>

        <div className="mt-16 relative">
          {/* Tracing beam */}
          <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-neutral-200">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              style={{ originX: 0 }}
              className="h-px bg-[#FF5A00]"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.n} delay={i * 0.12}>
                  <div data-testid={`process-step-${i}`} className="relative">
                    <div className="w-16 h-16 bg-[#FF5A00] flex items-center justify-center text-white relative z-10">
                      <Icon className="w-7 h-7" strokeWidth={1.6} />
                    </div>
                    <div className="mt-6 font-mono-kpi text-xs text-neutral-400 tracking-[0.24em]">{p.n}</div>
                    <h3 className="mt-2 font-tactical font-black uppercase text-2xl md:text-3xl tracking-tight">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-neutral-500 leading-relaxed">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}

function Gallery() {
  const [images, setImages] = useState(GALLERY_FALLBACK);
  const [idx, setIdx] = useState(0);
  const total = images.length;
  const next = React.useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = () => setIdx((i) => (i - 1 + total) % total);

  // Fetch dynamic gallery from backend (falls back silently to hardcoded list on error)
  useEffect(() => {
    let alive = true;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/site/gallery`;
    fetch(url, { credentials: "omit" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !Array.isArray(data) || data.length === 0) return;
        setImages(data.map((d) => d.url));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Autoplay
  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <Section id="trabajos" className="bg-[#0A0A0A] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Kicker>// Portafolio</Kicker>
          <h2 className="mt-4 font-tactical font-black uppercase tracking-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
            Lo que<br />hacemos.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 relative border border-white/10 bg-[#111]" data-testid="gallery-carousel">
            <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
              {images.map((src, i) => (
                <motion.img
                  key={src}
                  src={src}
                  alt={`Trabajo ${i + 1}`}
                  initial={false}
                  animate={{ opacity: idx === i ? 1 : 0, scale: idx === i ? 1 : 1.04 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid={`carousel-slide-${i}`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Prev / Next */}
            <button
              onClick={prev}
              aria-label="Anterior"
              data-testid="carousel-prev"
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-black/40 hover:bg-[#FF5A00] backdrop-blur-sm border border-white/15 hover:border-[#FF5A00] transition-colors"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
            </button>
            <button
              onClick={next}
              aria-label="Siguiente"
              data-testid="carousel-next"
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-black/40 hover:bg-[#FF5A00] backdrop-blur-sm border border-white/15 hover:border-[#FF5A00] transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
            </button>

            {/* Dots + counter */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-white/10">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/50">
                {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </div>
              <div className="flex items-center gap-1.5">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Ir al slide ${i + 1}`}
                    data-testid={`carousel-dot-${i}`}
                    className={`h-1 transition-all ${
                      idx === i ? "w-8 bg-[#FF5A00]" : "w-4 bg-white/25 hover:bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

function Contact() {
  const [data, setData] = useState({
    name: "", email: "", phone: "", company: "", reason: REASONS[0].value, message: "", consent: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!data.consent) {
      toast.error("Debes aceptar recibir información para continuar.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        name: data.name,
        email: data.email,
        phone: data.phone,
        company: data.company,
        message: data.message,
        consent: data.consent,
        reason: REASONS.find((r) => r.value === data.reason)?.label || data.reason,
      });
      setSent(true);
      toast.success("Mensaje enviado. Te contactamos en menos de 24h hábiles.");
    } catch (err) {
      toast.error("No pudimos enviar tu mensaje. Escríbenos a rodrigoyanez@allactive.cl");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/20 focus:border-[#FF5A00] outline-none px-4 py-3.5 text-white placeholder-white/40 text-sm transition-colors";

  return (
    <Section id="contacto" className="bg-[#0A0A0A] text-white border-t border-white/10">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Kicker>// Hablemos</Kicker>
            <h2 className="mt-4 font-tactical font-black uppercase tracking-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              Solicita<br />una visita.
            </h2>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Cuéntanos qué necesitas. Un especialista te contacta en menos de 24 horas hábiles con
              propuesta clara y sin compromiso.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href="mailto:rodrigoyanez@allactive.cl"
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-10 h-10 border border-white/20 group-hover:border-[#FF5A00] flex items-center justify-center transition-colors">
                  <Mail className="w-4 h-4 group-hover:text-[#FF5A00]" />
                </div>
                <div>
                  <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/40">Email</div>
                  <div className="text-sm text-white group-hover:text-[#FF5A00] transition-colors">
                    rodrigoyanez@allactive.cl
                  </div>
                </div>
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-whatsapp"
              >
                <div className="w-10 h-10 border border-white/20 group-hover:border-[#25D366] flex items-center justify-center transition-colors">
                  <MessageCircle className="w-4 h-4 group-hover:text-[#25D366]" />
                </div>
                <div>
                  <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/40">WhatsApp</div>
                  <div className="text-sm text-white group-hover:text-[#25D366] transition-colors">+56 9 8471 3814</div>
                </div>
              </a>
              <a
                href="https://instagram.com/allactive.cl"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-instagram"
              >
                <div className="w-10 h-10 border border-white/20 group-hover:border-[#FF5A00] flex items-center justify-center transition-colors">
                  <Instagram className="w-4 h-4 group-hover:text-[#FF5A00]" />
                </div>
                <div>
                  <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/40">Instagram</div>
                  <div className="text-sm text-white group-hover:text-[#FF5A00] transition-colors">@allactive.cl</div>
                </div>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            {sent ? (
              <div className="border border-[#FF5A00]/40 bg-[#FF5A00]/5 p-10 md:p-14" data-testid="contact-success">
                <Kicker>// Recibido</Kicker>
                <h3 className="mt-4 font-tactical font-black uppercase text-4xl md:text-5xl leading-tight">
                  Gracias, {data.name.split(" ")[0] || "amigo"}.
                </h3>
                <p className="mt-4 text-white/80 max-w-md">
                  Nuestro equipo revisó tu solicitud. Te contactamos en menos de 24 horas hábiles al
                  email <span className="text-[#FF5A00]">{data.email}</span>.
                </p>
                <button
                  onClick={() => { setSent(false); setData({ name: "", email: "", phone: "", company: "", reason: REASONS[0].value, message: "", consent: false }); }}
                  className="mt-8 font-mono-kpi text-[11px] uppercase tracking-[0.24em] text-white/60 hover:text-[#FF5A00] transition-colors"
                >
                  ← Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
                <input required data-testid="contact-name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="Nombre completo *" className={inputCls} />
                <input required type="email" data-testid="contact-email-input" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} placeholder="Correo electrónico *" className={inputCls} />
                <input data-testid="contact-phone" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} placeholder="Teléfono / WhatsApp" className={inputCls} />
                <input data-testid="contact-company" value={data.company} onChange={(e) => setData({ ...data, company: e.target.value })} placeholder="Empresa / Comunidad" className={inputCls} />
                <select required data-testid="contact-reason" value={data.reason} onChange={(e) => setData({ ...data, reason: e.target.value })} className={`${inputCls} md:col-span-2 appearance-none`}>
                  {REASONS.map((r) => (
                    <option key={r.value} value={r.value} className="bg-[#0A0A0A]">{r.label}</option>
                  ))}
                </select>
                <textarea required data-testid="contact-message" rows={5} value={data.message} onChange={(e) => setData({ ...data, message: e.target.value })} placeholder="Cuéntanos qué necesitas *" className={`${inputCls} md:col-span-2 resize-none`} />

                <label className="md:col-span-2 flex items-start gap-3 cursor-pointer group" data-testid="contact-consent-wrapper">
                  <input
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={data.consent}
                    onChange={(e) => setData({ ...data, consent: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-[#FF5A00] flex-shrink-0"
                  />
                  <span className="text-xs text-white/70 leading-relaxed group-hover:text-white/90 transition-colors">
                    <span className="text-[#FF5A00] mr-1">*</span>
                    Acepto recibir información de All Active sobre servicios, promociones y novedades. Puedo darme de baja en cualquier momento.
                  </span>
                </label>

                <div className="md:col-span-2 flex items-center justify-between gap-4 mt-2">
                  <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/40">
                    Los campos marcados con * son obligatorios
                  </div>
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={sending || !data.consent}
                    className="group inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E04D00] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono-kpi text-xs uppercase tracking-[0.24em] px-7 py-4 transition-colors"
                  >
                    {sending ? "Enviando…" : "Enviar mensaje"}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

function Footer() {
  return (
    <footer className="bg-black text-white pt-20 pb-10 px-6 md:px-10 lg:px-16 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <div className="font-tactical font-black uppercase tracking-tight text-[18vw] md:text-[13vw] lg:text-[11vw] leading-[0.85] text-white/5 select-none pointer-events-none">
          ALL ACTIVE
        </div>
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-4 gap-8 border-t border-white/10 pt-10">
          <div className="md:col-span-2">
            <Wordmark className="h-10 md:h-12 mb-5" />
            <p className="text-sm text-white/60 max-w-sm leading-relaxed">
              Mantenemos, reparamos y renovamos los gimnasios de las comunidades más exigentes de Chile.
            </p>
          </div>
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/40 mb-3">
              Contacto
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="block text-sm text-white/80 hover:text-[#25D366] transition-colors mb-2">
              WhatsApp +56 9 8471 3814
            </a>
            <a href="mailto:rodrigoyanez@allactive.cl" className="block text-sm text-white/80 hover:text-[#FF5A00] transition-colors mb-2">
              rodrigoyanez@allactive.cl
            </a>
            <a href="https://instagram.com/allactive.cl" target="_blank" rel="noreferrer" className="block text-sm text-white/80 hover:text-[#FF5A00] transition-colors">
              @allactive.cl
            </a>
          </div>
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/40 mb-3">
              Navegación
            </div>
            <a href="#segmentos" className="block text-sm text-white/80 hover:text-[#FF5A00] transition-colors mb-2">Segmentos</a>
            <a href="#servicios" className="block text-sm text-white/80 hover:text-[#FF5A00] transition-colors mb-2">Servicios</a>
            <a href="#proceso" className="block text-sm text-white/80 hover:text-[#FF5A00] transition-colors mb-2">Proceso</a>
            <a href="#contacto" className="block text-sm text-white/80 hover:text-[#FF5A00] transition-colors">Contacto</a>
          </div>
        </div>
        <div className="mt-14 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/40">
          <div>© {new Date().getFullYear()} All Active · Chile</div>
          <div>Servicio técnico profesional para gimnasios</div>
        </div>
      </div>
    </footer>
  );
}

// ---------- Root ----------
export default function LandingPage() {
  useEffect(() => {
    document.body.style.background = "#0A0A0A";
    const lenis = new Lenis({ smoothWheel: true, duration: 1.1 });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    const rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      document.body.style.background = "";
    };
  }, []);

  return (
    <div data-testid="landing-page" className="bg-[#0A0A0A] text-white min-h-screen">
      <Nav />
      <Hero />
      <Metrics />
      <Segments />
      <Services />
      <Process />
      <Gallery />
      <Contact />
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
